import { Box, HStack, SimpleGrid, Spinner, Text, VStack } from '@chakra-ui/react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Sparkles, TriangleAlert } from 'lucide-react';
import CustomButton from '@/components/atoms/CustomButton';
import { MAX_CONTAINER_WIDTH } from '@/constants/dimensions';
import { contentCreatorRoutes } from '@/constants/routes/app';
import useInfiniteScrollSentinel from '@/hooks/useInfiniteScrollSentinel';
import useMyContent from '@/hooks/content/useMyContent';
import ContentCard from '@/modules/content/common/ContentCard';
import ContentCardSkeletonGrid from '@/modules/content/common/ContentCardSkeletonGrid';
import ContentEmptyState from '@/modules/content/common/ContentEmptyState';
import { getContentDisplayStatus } from '@/modules/content/common/ContentStatusBadge';

const CARD_SIZE = { xl: '100%', sm: '280px' } as const;

const CreatorLibrary = () => {
  const navigate = useNavigate();
  const { content, isLoading, isError, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } = useMyContent();
  const loadMoreRef = useInfiniteScrollSentinel({ hasNextPage: !!hasNextPage, isFetchingNextPage, fetchNextPage });

  return (
    <VStack width={MAX_CONTAINER_WIDTH} paddingBottom="48px" alignItems="stretch" gap="24px">
      <HStack justifyContent="space-between" alignItems="center">
        <Text as="h1" variant="loraMediumTitle" color="text.darkBlue">
          My Library
        </Text>
        {content.length > 0 && (
          <CustomButton
            label="Create New Content"
            backgroundColor="primary.500"
            labelColor="extra.white"
            width="240px"
            onClick={() => navigate({ to: contentCreatorRoutes.creatorContentCreate })}
          />
        )}
      </HStack>

      {isLoading ? (
        <ContentCardSkeletonGrid count={6} cardSize={CARD_SIZE} />
      ) : isError ? (
        <ContentEmptyState
          icon={TriangleAlert}
          title="Couldn’t load your content"
          description="Something went wrong while loading your library. Please try again."
          actionLabel="Try again"
          onAction={() => refetch()}
        />
      ) : content.length === 0 ? (
        <ContentEmptyState
          icon={Sparkles}
          title="No content yet"
          description="Content you create will appear here, drafts included."
          actionLabel="Create New Content"
          onAction={() => navigate({ to: contentCreatorRoutes.creatorContentCreate })}
        />
      ) : (
        <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} gap="20px" paddingX="12px" alignItems="start">
          {content.map((item) => (
            <Box
              key={item.id}
              role="group"
              cursor="pointer"
              borderRadius="14px"
              transition="transform 0.25s ease, box-shadow 0.25s ease"
              onClick={() => {
                if (item.status === 'draft') {
                  navigate({ to: '/creator/content/$contentId', params: { contentId: item.id } });
                  return;
                }
                navigate({ to: '/loop/$contentId', params: { contentId: item.id }, search: { source: 'library' } });
              }}
            >
              <ContentCard
                content={item}
                size={CARD_SIZE}
                compact
                shouldRaiseOnHover
                status={getContentDisplayStatus(item)}
              />
            </Box>
          ))}
        </SimpleGrid>
      )}

      {content.length > 0 && hasNextPage && (
        <Box ref={loadMoreRef} width="100%">
          {isFetchingNextPage && (
            <HStack justifyContent="center" paddingY="24px">
              <Spinner color="primary.500" size="md" />
            </HStack>
          )}
        </Box>
      )}
    </VStack>
  );
};

export const Route = createFileRoute('/_authenticated/_creator/creator/library')({
  staticData: { title: 'My Library' },
  component: CreatorLibrary,
});
